import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  FormControl
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

function TimelineAdjustmentModal({ 
  open, 
  onClose, 
  onConfirm, 
  oldMonths, 
  newMonths, 
  oldStartMonth, 
  newStartMonth 
}) {
  const [adjustmentChoice, setAdjustmentChoice] = useState('auto-adjust');

  // Reset to auto-adjust when modal opens
  useEffect(() => {
    if (open) {
      setAdjustmentChoice('auto-adjust');
    }
  }, [open]);

  const handleConfirm = () => {
    console.log(`Timeline modal confirmed with choice: ${adjustmentChoice}`);
    onConfirm(adjustmentChoice);
    onClose();
  };

  const getTimelineChangeDescription = () => {
    if (oldStartMonth !== newStartMonth && oldMonths !== newMonths) {
      return `You've changed the project timeline from ${oldStartMonth} (${oldMonths} months) to ${newStartMonth} (${newMonths} months).`;
    } else if (oldStartMonth !== newStartMonth) {
      return `You've changed the project start from ${oldStartMonth} to ${newStartMonth}.`;
    } else if (oldMonths !== newMonths) {
      return `You've changed the project duration from ${oldMonths} months to ${newMonths} months.`;
    }
    return 'You\'ve changed the project timeline.';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Project Timeline Changed</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {getTimelineChangeDescription()}
          How would you like to handle existing role assignments?
        </Typography>
        
        <FormControl component="fieldset">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box 
              sx={{ 
                p: 2, 
                border: '1px solid', 
                borderColor: adjustmentChoice === 'auto-adjust' ? 'primary.main' : 'divider',
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: adjustmentChoice === 'auto-adjust' ? 'action.selected' : 'transparent'
              }}
              onClick={() => setAdjustmentChoice('auto-adjust')}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Auto-adjust role timelines (Recommended)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Roles using default timeline will automatically update. Custom timelines will be preserved.
              </Typography>
            </Box>
            
            <Box 
              sx={{ 
                p: 2, 
                border: '1px solid', 
                borderColor: adjustmentChoice === 'keep-as-is' ? 'primary.main' : 'divider',
                borderRadius: 1,
                cursor: 'pointer',
                bgcolor: adjustmentChoice === 'keep-as-is' ? 'action.selected' : 'transparent'
              }}
              onClick={() => setAdjustmentChoice('keep-as-is')}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Keep current role assignments as-is
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maintain all existing role timelines without any changes.
              </Typography>
            </Box>
          </Box>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained">
          Apply Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TimelineAdjustmentModal;